import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { testimonialService } from '../services/testimonial.service'
import type { ClientEngagementDocument } from '@/types'

type EngagementWithId = ClientEngagementDocument & { id: string }

interface UseSurveyPageResult {
  engagement: EngagementWithId | null
  alreadySubmitted: boolean
  eligible: boolean
  loading: boolean
  error: string | null
}

export function useSurveyPage(): UseSurveyPageResult {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const engagementId = searchParams.get('engagement')

  const [engagement, setEngagement] = useState<EngagementWithId | null>(null)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [eligible, setEligible] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!engagementId || !user) {
      setLoading(false)
      setError(withErrorCode('Invalid or missing survey link.', 'app/validation'))
      return
    }

    setLoading(true)
    setError(null)

    const ref = typedDoc<ClientEngagementDocument>('clientEngagements', engagementId)

    Promise.all([
      getDocument(ref),
      testimonialService.getByEngagementAndUser(engagementId, user.uid),
    ])
      .then(([eng, existing]) => {
        if (!eng) {
          setError(withErrorCode('This survey link is not valid.', 'app/validation'))
          setEligible(false)
          return
        }

        const engWithId: EngagementWithId = { ...eng, id: engagementId }
        setEngagement(engWithId)

        // Must belong to this user and be in 'delivered' status
        const isOwner = eng.clientUserId === user.uid
        const isDelivered = eng.status === 'delivered'

        if (!isOwner || !isDelivered) {
          setEligible(false)
          return
        }

        if (existing) {
          setAlreadySubmitted(true)
          setEligible(true)
          return
        }

        setEligible(true)
      })
      .catch((err: unknown) => {
        setError(formatUserFacingError(err, 'Failed to load survey.'))
        setEligible(false)
      })
      .finally(() => setLoading(false))
  }, [engagementId, user])

  return { engagement, alreadySubmitted, eligible, loading, error }
}
